import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, X, Check, Network, Loader2, AlertTriangle } from 'lucide-react'
import { remoteNetworksApi } from '../../../../services/remoteNetworksApi'
import { cidrContains } from '../../../../utils/cidr'

/**
 * Lista CIDRs declarados como "redes do lado do device" (LAN atrás do peer).
 * Cada linha tem dois toggles:
 *   - Enabled: quando false, o platform ignora a entrada (não emite rota nem regra NAT no router,
 *     e o CIDR sai das AllowedIPs do peer no servidor VPN).
 *   - Masquerade: quando true, o `Automais.IO.routeros` provisiona automaticamente uma regra
 *     `/ip/firewall/nat chain=srcnat action=masquerade in-interface=<wg> dst-address=<cidr>` no MK.
 *     Necessário quando hosts dentro do CIDR não têm rota de retorno para a rede VPN.
 *     Só existe para router: em host nenhum satélite consome o flag (o masquerade do vpnserver é
 *     dirigido por main_interface/cross-wg, não por esta coluna), então o toggle não é renderizado.
 *
 * Sync tem DOIS escopos e cada tipo de device confirma por um caminho diferente:
 *   - `syncStatus`       ← só o `Automais.IO.routeros`, ao aplicar a regra NAT no MikroTik.
 *   - `serverSyncStatus` ← o `Automais.IO.vpnserver`, ao aplicar AllowedIPs/nftables.
 * Host não tem routeros, então seu `syncStatus` nunca sai de Pending — olhar esse campo em host
 * deixava o badge "aguardando" girando para sempre e o polling sem fim.
 */
export default function RemoteNetworksSection({ device, deviceType }) {
  const { t } = useTranslation()
  const isRouter = deviceType === 'router'

  /** Status que representa a convergência real deste tipo de device. */
  const statusEfetivo = useCallback(
    (rn) => (isRouter ? rn?.syncStatus : rn?.serverSyncStatus) ?? null,
    [isRouter],
  )

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ networkCidr: '', description: '', enabled: true, masquerade: false })
  const [errors, setErrors] = useState({})

  const load = useCallback(async () => {
    if (!device?.id) return
    setLoading(true)
    try {
      const data = isRouter
        ? await remoteNetworksApi.getByRouter(device.id)
        : await remoteNetworksApi.getByHost(device.id)
      setItems(data || [])
    } catch (e) {
      console.error('remoteNetworks load:', e)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [device?.id, isRouter])

  useEffect(() => { load() }, [load])

  // Polling silencioso enquanto houver item pendente no escopo que vale para este device —
  // routeros confirma no MK (até 60s/ciclo), vpnserver confirma AllowedIPs/nftables.
  // Refetch sem setLoading(true) → não pisca tela.
  const pollTimerRef = useRef(null)
  useEffect(() => {
    const hasPending = items.some((rn) => statusEfetivo(rn) === 'Pending')
    if (!hasPending || !device?.id) {
      if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null }
      return
    }
    pollTimerRef.current = setTimeout(async () => {
      try {
        const data = isRouter
          ? await remoteNetworksApi.getByRouter(device.id)
          : await remoteNetworksApi.getByHost(device.id)
        setItems(data || [])
      } catch { /* silencioso — próximo tick tenta de novo */ }
    }, 5000)
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current) }
  }, [items, device?.id, isRouter, statusEfetivo])

  const handleAdd = async () => {
    const cidr = form.networkCidr.trim()
    if (!cidr) {
      setErrors({ networkCidr: t('config.cidrRequired') })
      return
    }
    try {
      await remoteNetworksApi.create(deviceType, device.id, {
        networkCidr: cidr,
        description: form.description.trim() || null,
        enabled: form.enabled,
        masquerade: form.masquerade,
      })
      setForm({ networkCidr: '', description: '', enabled: true, masquerade: false })
      setErrors({})
      setAdding(false)
      await load()
    } catch (e) {
      setErrors({ networkCidr: e.response?.data?.message || t('config.networksAddError') })
    }
  }

  const handleDelete = async (id) => {
    try {
      await remoteNetworksApi.delete(deviceType, device.id, id)
      await load()
    } catch (e) {
      alert(e.response?.data?.message || t('config.networksDeleteError'))
    }
  }

  const handleToggle = async (rn, field) => {
    const newValue = !rn[field]
    // Optimistic update — flag visual flipa imediatamente; os dois escopos de sync viram
    // Pending (é o que o backend faz ao mudar enforcement) e o badge "aguardando" aparece até
    // o satélite correspondente confirmar (polling acima detecta).
    setItems((curr) => curr.map((it) =>
      it.id === rn.id ? { ...it, [field]: newValue, syncStatus: 'Pending', serverSyncStatus: 'Pending' } : it
    ))
    try {
      await remoteNetworksApi.update(deviceType, device.id, rn.id, { [field]: newValue })
    } catch (e) {
      // Reverte
      setItems((curr) => curr.map((it) =>
        it.id === rn.id ? { ...it, [field]: !newValue, syncStatus: rn.syncStatus, serverSyncStatus: rn.serverSyncStatus } : it
      ))
      alert(e.response?.data?.message || t('config.networksUpdateError') || 'Falha ao atualizar')
    }
  }

  // IPs que o device reporta na telemetria e que nenhuma rede habilitada cobre. Enquanto um
  // deles ficar de fora, o peer não carrega esse endereço nas AllowedIPs: quem falar com o
  // device por ali recebe o pedido entregue e a resposta descartada no servidor VPN.
  const enderecosNaoDeclarados = useMemo(() => {
    let telemetria
    try {
      telemetria = JSON.parse(device?.metricsJson || '{}')
    } catch {
      return []
    }
    const enderecos = telemetria?.network?.addresses
    if (!Array.isArray(enderecos)) return []

    const cobertos = items.filter((rn) => rn.enabled).map((rn) => rn.networkCidr)
    return enderecos.filter((endereco) => {
      const ip = String(endereco?.cidr || '').split('/')[0]
      if (!ip) return false
      return !cobertos.some((rede) => cidrContains(rede, ip))
    })
  }, [device?.metricsJson, items])

  const handleDeclarar = async (endereco) => {
    const ip = String(endereco.cidr).split('/')[0]
    try {
      // /32 e não a rede inteira: o servidor VPN resolve AllowedIPs por prefixo mais longo, e
      // declarar a faixa toda sequestra para este túnel vizinhos que são alcançados por outro
      // caminho. O campo manual continua aberto para quem realmente quer a faixa.
      await remoteNetworksApi.create(deviceType, device.id, {
        networkCidr: `${ip}/32`,
        description: endereco.interface || null,
        enabled: true,
        masquerade: false,
      })
      await load()
    } catch (e) {
      alert(e.response?.data?.message || t('config.networksAddError'))
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Network className="w-4 h-4 text-theme-primary" />
        <h3 className="text-sm font-semibold text-gray-900">
          {t(isRouter ? 'config.networksTitleRouter' : 'config.networksTitleHost')}
        </h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        {t(isRouter ? 'config.networksSubtitleRouter' : 'config.networksSubtitleHost')}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('config.loading')}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((rn) => (
            <div key={rn.id} className={`flex items-center justify-between gap-3 p-2 border rounded ${rn.enabled ? 'bg-gray-50 border-gray-200' : 'bg-gray-100 border-gray-200 opacity-60'}`}>
              <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-mono font-medium text-gray-900">{rn.networkCidr}</span>
                {rn.description && (
                  <span className="text-xs text-gray-500">— {rn.description}</span>
                )}
                {/* Router confirma em dois lugares (MikroTik e servidor VPN) e o escopo é
                    rotulado; host só tem o servidor, então o badge vai sem rótulo. */}
                {(isRouter
                  ? [
                      { status: rn.syncStatus, escopo: t('config.networksScopeRouter', { defaultValue: 'router' }) },
                      { status: rn.serverSyncStatus, escopo: t('config.networksScopeServer', { defaultValue: 'servidor' }) },
                    ]
                  : [{ status: rn.serverSyncStatus, escopo: null }]
                ).map(({ status, escopo }) => {
                  if (status === 'Pending') {
                    return (
                      <span key={escopo || 'server'} className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 font-medium">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {t('common.syncing', { defaultValue: 'aguardando' })}{escopo ? ` (${escopo})` : ''}
                      </span>
                    )
                  }
                  if (status === 'Error') {
                    return (
                      <span key={escopo || 'server'} className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-100 border border-red-300 rounded px-1.5 py-0.5 font-medium">
                        {t('common.syncError', { defaultValue: 'erro de sync' })}{escopo ? ` (${escopo})` : ''}
                      </span>
                    )
                  }
                  return null
                })}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!rn.enabled}
                    onChange={() => handleToggle(rn, 'enabled')}
                    className="w-3.5 h-3.5 accent-theme-primary"
                  />
                  {t('common.enable', { defaultValue: 'Habilita' })}
                </label>
                {isRouter && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none" title={t('config.networksMasqueradeHint') || 'Mascarar origem da VPN como IP local do MK ao alcançar este CIDR (necessário se hosts não têm rota de retorno).'}>
                    <input
                      type="checkbox"
                      checked={!!rn.masquerade}
                      onChange={() => handleToggle(rn, 'masquerade')}
                      className="w-3.5 h-3.5 accent-theme-primary"
                    />
                    Masquerade
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(rn.id)}
                  className="p-1 hover:bg-red-50 rounded"
                  aria-label={t('config.delete') || 'Remover'}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            </div>
          ))}

          {adding ? (
            <div className="p-3 bg-white border border-theme-primary/30 rounded space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={t('config.networksCidrPlaceholder')}
                  className={`input flex-1 text-sm ${errors.networkCidr ? 'border-red-500' : ''}`}
                  value={form.networkCidr}
                  onChange={(e) => { setForm((f) => ({ ...f, networkCidr: e.target.value })); setErrors({}) }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
                <input
                  type="text"
                  placeholder={t('config.descriptionPlaceholder')}
                  className="input flex-1 text-sm"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              {errors.networkCidr && <p className="text-xs text-red-600">{errors.networkCidr}</p>}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                      className="w-3.5 h-3.5 accent-theme-primary"
                    />
                    {t('common.enable', { defaultValue: 'Habilita' })}
                  </label>
                  {isRouter && (
                    <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none" title={t('config.networksMasqueradeHint') || 'Mascarar origem da VPN como IP local do MK ao alcançar este CIDR.'}>
                      <input
                        type="checkbox"
                        checked={form.masquerade}
                        onChange={(e) => setForm((f) => ({ ...f, masquerade: e.target.checked }))}
                        className="w-3.5 h-3.5 accent-theme-primary"
                      />
                      Masquerade
                    </label>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => { setAdding(false); setForm({ networkCidr: '', description: '', enabled: true, masquerade: false }); setErrors({}) }} className="p-1.5 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                  <button type="button" onClick={handleAdd} className="p-1.5 hover:bg-green-50 rounded">
                    <Check className="w-4 h-4 text-green-600" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setAdding(true)} className="flex items-center gap-1.5 text-xs text-theme-primary hover:text-theme-primary/80 font-medium mt-1">
              <Plus className="w-3.5 h-3.5" />
              {t('config.networksAdd')}
            </button>
          )}

          {enderecosNaoDeclarados.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <h4 className="text-xs font-semibold text-amber-900">
                  {t('config.networksDetectedTitle', { defaultValue: 'IPs deste device fora da VPN' })}
                </h4>
              </div>
              <p className="text-xs text-amber-800 mb-2">
                {t('config.networksDetectedHint', {
                  defaultValue: 'Estes endereços existem no device mas não estão declarados aqui. Quem falar com o device por eles tem o pedido entregue e a resposta descartada — a conexão morre em um sentido só.',
                })}
              </p>
              <div className="space-y-1.5">
                {enderecosNaoDeclarados.map((endereco) => (
                  <div key={`${endereco.interface}-${endereco.cidr}`} className="flex items-center justify-between gap-3">
                    <span className="text-xs font-mono text-amber-900">
                      {endereco.cidr}
                      {endereco.interface && <span className="text-amber-700"> — {endereco.interface}</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeclarar(endereco)}
                      className="flex items-center gap-1 text-xs text-amber-900 hover:text-amber-950 font-medium border border-amber-400 rounded px-2 py-0.5 hover:bg-amber-100"
                    >
                      <Plus className="w-3 h-3" />
                      {t('config.networksDetectedAdd', { defaultValue: 'Declarar' })}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
